/**
 * chainVideoExport.js
 *
 * Records the chain reveal sequence as a square WebM video.
 *
 * The reveal plays as a sequence of full-frame "slides", one per step:
 * each slide announces "{name} wrote/drew" and then uses the whole frame to
 * show what they wrote or drew. The frame clears between steps.
 *
 * Fast path  : WebCodecs API (VideoEncoder + webm-muxer)
 *              Renders frames faster than real-time (no wall-clock waiting).
 *              Supported in Chrome/Edge 94+.
 *
 * Slow path  : canvas.captureStream() + MediaRecorder — real-time, all browsers.
 */

/* global VideoEncoder, VideoFrame */
import { Muxer, ArrayBufferTarget } from 'webm-muxer';
import { renderStrokes, trimDrawingData } from './drawingReplay';

// ── Canvas / video config ─────────────────────────────────────────────────────
const SIZE          = 720;   // square px

const FPS           = 30;
const VIDEO_BITRATE = 6_000_000;

// Replay timing (ms)
const PLAYBACK_SPEED = 5;     // drawing replay speed multiplier
const TYPING_DELAY   = 900;   // "{name} wrote/drew" announcement beat
const TEXT_LINGER    = 2600;  // hold on revealed text
const DRAW_LINGER    = 1400;  // hold after a drawing finishes
const END_PAUSE      = 2400;  // hold on the final slide

// ── Design tokens — Sketchpad (mirrors client/src/index.css) ──────────────────
const C = {
  bg:       '#FFFEF0',   // soft cream
  bg2:      '#FFF7C9',   // brand-yellow wash
  card:     '#FFFFFF',   // white card
  ink:      '#5A4800',   // warm brown ink stroke
  inkSoft:  '#8A7028',
  red:      '#EB605C',   // accent coral
  teal:     '#3594C0',   // link blue
  marig:    '#FBE67A',   // brand yellow
  plum:     '#5A8030',   // primary green (legacy plum slot)
  leaf:     '#5A8030',   // primary green
  drawBox:  '#FFFEF0',   // cream behind drawings
};

const BODY  = `'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
const TITLE = `'Darumadrop One', 'Fredoka', 'Baloo 2', system-ui, sans-serif`;
const HAND  = `'Patrick Hand', 'Caveat', cursive`;

// Per-step accent — colours the tape on each drawing slide.
const ACCENTS = [C.red, C.teal, C.marig, C.plum, C.leaf];

// ── Layout ─────────────────────────────────────────────────────────────────────
const PAGE_PAD     = 56;    // outer margin for slide content
const ATTR_CY      = 116;   // vertical centre of the attribution row
const CONTENT_TOP  = 196;   // top of the content area
const CONTENT_BOT  = 676;   // bottom of the content area

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

// ── Helpers ───────────────────────────────────────────────────────────────────
function sf(ctx, weight, size, family = BODY) {
  ctx.font = `${weight} ${size}px ${family}`;
}

function wrapText(ctx, text, maxW) {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxW) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}

function rrect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, r);
  } else {
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }
}

// ── Shared setup ──────────────────────────────────────────────────────────────

/** Pre-compute per-step drawing canvases and the flat timeline. */
function buildSetup(steps) {
  const drawInfos = steps.map(step => {
    if (step.type !== 'drawing' || !step.drawing_data) return null;
    const trimmedData = trimDrawingData(step.drawing_data);
    const cfg = step.drawing_data.canvas || {};
    const c   = document.createElement('canvas');
    c.width   = cfg.width  || 1200;
    c.height  = cfg.height || 900;
    return { canvas: c, ctx: c.getContext('2d'), trimmedData };
  });

  const timeline = [];
  let t = 0;
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    timeline.push({ stepIdx: i, phase: 'typing', start: t, end: t + TYPING_DELAY });
    t += TYPING_DELAY;

    if (step.type === 'drawing') {
      const drawDur = (drawInfos[i]?.trimmedData?.totalDuration || 3000) / PLAYBACK_SPEED;
      timeline.push({ stepIdx: i, phase: 'drawing', start: t, end: t + drawDur });
      t += drawDur;
      timeline.push({ stepIdx: i, phase: 'linger', start: t, end: t + DRAW_LINGER });
      t += DRAW_LINGER;
    } else {
      timeline.push({ stepIdx: i, phase: 'text', start: t, end: t + TEXT_LINGER });
      t += TEXT_LINGER;
    }
  }
  const totalDuration = t + END_PAUSE;

  return { drawInfos, timeline, totalDuration };
}

// ── Drawing primitives ────────────────────────────────────────────────────────

/** Cream paper background (warm radial gradient + grain) — built once, reused. */
let _paper = null;
function getPaper() {
  if (_paper) return _paper;
  const c = document.createElement('canvas');
  c.width = SIZE;
  c.height = SIZE;
  const x = c.getContext('2d');

  const g = x.createRadialGradient(SIZE * 0.2, 0, 0, SIZE * 0.2, 0, SIZE);
  g.addColorStop(0, C.bg2);
  g.addColorStop(0.6, C.bg);
  g.addColorStop(1, C.bg);
  x.fillStyle = g;
  x.fillRect(0, 0, SIZE, SIZE);

  // Paper grain — faint ink dots on a 14px grid
  x.fillStyle = 'rgba(35, 33, 28, 0.06)';
  for (let yy = 1; yy < SIZE; yy += 14) {
    for (let xx = 1; xx < SIZE; xx += 14) {
      x.beginPath();
      x.arc(xx, yy, 1, 0, Math.PI * 2);
      x.fill();
    }
  }
  _paper = c;
  return c;
}

/** Ink-bordered cream avatar circle with a hard sticker shadow. */
function drawAvatar(ctx, cx, cy, r, step) {
  const isMascot = typeof step.player_avatar === 'string' && /^m[0-9]+$/.test(step.player_avatar);
  const glyph = (!isMascot && step.player_avatar) || step.player_name?.charAt(0)?.toUpperCase() || '?';

  ctx.beginPath();
  ctx.arc(cx + 2, cy + 2, r, 0, Math.PI * 2);
  ctx.fillStyle = C.ink;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = C.card;
  ctx.fill();
  ctx.lineWidth   = 2;
  ctx.strokeStyle = C.ink;
  ctx.stroke();

  ctx.fillStyle    = C.ink;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  if (/^[A-Z0-9]$/i.test(glyph)) {
    sf(ctx, '400', Math.round(r), TITLE);
    ctx.fillText(glyph, cx, cy + 1);
  } else {
    ctx.font = `${Math.round(r * 1.2)}px serif`;
    ctx.fillText(glyph, cx, cy + 1);
  }
}

/** A torn strip of washi tape, rotated, with diagonal stripes. */
function drawTape(ctx, x, y, color) {
  const w = 64, h = 20;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(-7 * Math.PI / 180);

  rrect(ctx, 0, 0, w, h, 2);
  ctx.globalAlpha = 0.9;
  ctx.fillStyle = color;
  ctx.fill();

  ctx.save();
  rrect(ctx, 0, 0, w, h, 2);
  ctx.clip();
  ctx.globalAlpha = 0.18;
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1;
  for (let i = -h; i < w; i += 5) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i + h, h);
    ctx.stroke();
  }
  ctx.restore();

  ctx.globalAlpha = 1;
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(35, 33, 28, 0.25)';
  rrect(ctx, 0, 0, w, h, 2);
  ctx.stroke();
  ctx.restore();
}

/** Centred "{avatar} {name} {action}" attribution row near the top. */
function drawAttribution(ctx, step) {
  const r      = 22;
  const name   = step.player_name || '';
  const action = step.type === 'prompt' ? ' wrote' : step.type === 'guess' ? ' guessed' : ' drew';

  sf(ctx, '400', 30, TITLE);
  const nameW = ctx.measureText(name).width;
  sf(ctx, '700', 27, HAND);
  const actW = ctx.measureText(action).width;

  const gap     = 14;
  const totalW  = r * 2 + gap + nameW + actW;
  let x = SIZE / 2 - totalW / 2;

  drawAvatar(ctx, x + r, ATTR_CY, r, step);
  x += r * 2 + gap;

  ctx.textAlign    = 'left';
  ctx.textBaseline = 'middle';
  sf(ctx, '400', 30, TITLE);
  ctx.fillStyle = C.ink;
  ctx.fillText(name, x, ATTR_CY + 1);
  x += nameW;
  sf(ctx, '700', 27, HAND);
  ctx.fillStyle = C.red;
  ctx.fillText(action, x, ATTR_CY + 1);
}

/** Bouncing ink dots in the content area (the announcement beat). */
function drawDots(ctx, animNow) {
  const cy = (CONTENT_TOP + CONTENT_BOT) / 2;
  const cx = SIZE / 2 - 26;
  ctx.fillStyle = C.ink;
  for (let i = 0; i < 3; i++) {
    const bounce = Math.sin((animNow * 0.005 + i * 0.5) * Math.PI) * 6;
    ctx.beginPath();
    ctx.arc(cx + i * 26, cy - bounce, 7, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** The written quote, auto-sized to fill the content area, popped in. */
function drawQuote(ctx, step, pop) {
  const text = step.text ? `“${step.text}”` : '“”';
  const maxW = SIZE - PAGE_PAD * 2;
  const maxH = CONTENT_BOT - CONTENT_TOP;

  // Shrink the display font until the wrapped quote fits the content area.
  let size = 72;
  let lines = [text];
  let lineH = size * 1.16;
  for (; size >= 26; size -= 2) {
    sf(ctx, '400', size, TITLE);
    lineH = size * 1.16;
    lines = wrapText(ctx, text, maxW);
    if (lines.length * lineH <= maxH) break;
  }

  const cx = SIZE / 2;
  const cy = (CONTENT_TOP + CONTENT_BOT) / 2;
  const totalH = lines.length * lineH;

  ctx.save();
  ctx.globalAlpha *= pop;
  ctx.translate(cx, cy);
  ctx.scale(0.86 + 0.14 * pop, 0.86 + 0.14 * pop);
  ctx.translate(-cx, -cy);

  sf(ctx, '400', size, TITLE);
  ctx.fillStyle    = C.ink;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  let y = cy - totalH / 2 + lineH / 2;
  for (const ln of lines) {
    ctx.fillText(ln, cx, y);
    y += lineH;
  }
  ctx.restore();
}

/** The drawing, filling the content area inside an ink frame with a tape strip. */
function drawDrawing(ctx, step, drawInfo, stepIdx, drawElapsed) {
  const maxW = SIZE - PAGE_PAD * 2;
  const maxH = CONTENT_BOT - CONTENT_TOP;

  if (!drawInfo) {
    sf(ctx, '700', 28, HAND);
    ctx.fillStyle    = C.inkSoft;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('(no drawing)', SIZE / 2, (CONTENT_TOP + CONTENT_BOT) / 2);
    return;
  }

  const { canvas, ctx: dctx, trimmedData } = drawInfo;
  const bg = trimmedData?.canvas?.background || '#ffffff';
  const t  = drawElapsed === Infinity ? Infinity : drawElapsed * PLAYBACK_SPEED;
  renderStrokes(dctx, trimmedData.strokes, t, bg);

  // Fit the drawing into the content box, preserving aspect ratio.
  const aspect = canvas.width / canvas.height;
  let w = maxW;
  let h = w / aspect;
  if (h > maxH) { h = maxH; w = h * aspect; }
  const x = (SIZE - w) / 2;
  const y = CONTENT_TOP + (maxH - h) / 2;

  const tilt = (stepIdx % 2 === 0 ? -1 : 1) * 1.1 * Math.PI / 180;
  ctx.save();
  ctx.translate(x + w / 2, y + h / 2);
  ctx.rotate(tilt);
  ctx.translate(-(x + w / 2), -(y + h / 2));

  // Sticker shadow
  rrect(ctx, x + 5, y + 5, w, h, 10);
  ctx.fillStyle = C.ink;
  ctx.fill();

  // Cream frame + clipped drawing
  rrect(ctx, x, y, w, h, 10);
  ctx.fillStyle = C.drawBox;
  ctx.fill();
  ctx.save();
  rrect(ctx, x, y, w, h, 10);
  ctx.clip();
  ctx.drawImage(canvas, x, y, w, h);
  ctx.restore();

  // Ink border
  rrect(ctx, x, y, w, h, 10);
  ctx.lineWidth   = 3;
  ctx.strokeStyle = C.ink;
  ctx.stroke();

  // Tape across the top edge
  drawTape(ctx, x + w / 2 - 32, y - 13, ACCENTS[stepIdx % ACCENTS.length]);
  ctx.restore();
}

/** Render a single full-frame slide for whichever step is active at `elapsed`. */
function renderFrame(ctx, steps, timeline, drawInfos, elapsed) {
  // Current timeline entry (falls back to the last one during END_PAUSE).
  let entry = timeline[timeline.length - 1];
  for (const e of timeline) {
    if (elapsed >= e.start && elapsed < e.end) { entry = e; break; }
  }
  const { stepIdx, phase, start: phaseStart } = entry;
  const step = steps[stepIdx];
  const phaseElapsed = elapsed - phaseStart;

  // When did this step's slide begin (its "typing" phase)?
  let stepStart = entry.start;
  for (const e of timeline) {
    if (e.stepIdx === stepIdx) { stepStart = e.start; break; }
  }

  // Paper background
  ctx.drawImage(getPaper(), 0, 0);

  // Whole-slide fade + rise as each new step appears
  const fade = Math.min(1, (elapsed - stepStart) / 300);
  ctx.save();
  ctx.globalAlpha = fade;
  ctx.translate(0, (1 - easeOut(fade)) * 16);

  drawAttribution(ctx, step);

  if (phase === 'typing') {
    drawDots(ctx, elapsed);
  } else if (step.type === 'prompt' || step.type === 'guess') {
    const pop = easeOut(Math.min(1, phaseElapsed / 280));
    drawQuote(ctx, step, pop);
  } else if (step.type === 'drawing') {
    const drawEl = phase === 'drawing' ? phaseElapsed : Infinity;
    drawDrawing(ctx, step, drawInfos[stepIdx], stepIdx, drawEl);
  }

  ctx.restore();
}

// ── Fast path: WebCodecs + webm-muxer ────────────────────────────────────────
async function recordWithWebCodecs(chain, onProgress) {
  const steps = chain.steps || [];
  const { drawInfos, timeline, totalDuration } = buildSetup(steps);

  const canvas = document.createElement('canvas');
  canvas.width  = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');

  const target = new ArrayBufferTarget();
  const muxer  = new Muxer({
    target,
    video: {
      codec:     'V_VP9',
      width:     SIZE,
      height:    SIZE,
      frameRate: FPS,
    },
    firstTimestampBehavior: 'offset',
  });

  let encoderError = null;
  const encoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error:  (e) => { encoderError = e; },
  });

  encoder.configure({
    codec:     'vp09.00.10.08',
    width:     SIZE,
    height:    SIZE,
    bitrate:   VIDEO_BITRATE,
    framerate: FPS,
  });

  const frameDurMs    = 1000 / FPS;
  const totalFrames   = Math.ceil(totalDuration / frameDurMs);

  for (let fi = 0; fi < totalFrames; fi++) {
    if (encoderError) throw encoderError;

    const elapsed   = fi * frameDurMs;
    const tsMicros  = Math.round(elapsed * 1000);
    const durMicros = Math.round(frameDurMs * 1000);

    renderFrame(ctx, steps, timeline, drawInfos, elapsed);

    const vf = new VideoFrame(canvas, { timestamp: tsMicros, duration: durMicros });
    encoder.encode(vf, { keyFrame: fi % (FPS * 2) === 0 });
    vf.close();

    if (onProgress) onProgress(Math.min(elapsed / totalDuration, 0.99));

    // Yield every 30 frames to keep the UI responsive and drain the encode queue
    if (fi % 30 === 0) {
      await new Promise(r => setTimeout(r, 0));
    }
  }

  await encoder.flush();
  muxer.finalize();

  if (onProgress) onProgress(1);

  const blob = new Blob([target.buffer], { type: 'video/webm' });
  return { blob, mimeType: 'video/webm' };
}

// ── Slow path: MediaRecorder (real-time) ──────────────────────────────────────
function getSupportedMimeType() {
  const candidates = [
    'video/mp4;codecs=h264,aac',
    'video/mp4;codecs=h264',
    'video/mp4',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  return candidates.find(t => MediaRecorder.isTypeSupported(t)) || 'video/webm';
}

function recordWithMediaRecorder(chain, onProgress) {
  const steps = chain.steps || [];
  const { drawInfos, timeline, totalDuration } = buildSetup(steps);

  const canvas = document.createElement('canvas');
  canvas.width  = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');

  const mimeType = getSupportedMimeType();
  const stream   = canvas.captureStream(FPS);
  const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: VIDEO_BITRATE });
  const chunks   = [];
  recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };

  return new Promise((resolve, reject) => {
    recorder.onstop  = () => resolve({ blob: new Blob(chunks, { type: mimeType }), mimeType });
    recorder.onerror = reject;
    recorder.start(200);

    const startTime = performance.now();

    function tick() {
      const now     = performance.now();
      const elapsed = now - startTime;

      if (onProgress) onProgress(Math.min(elapsed / totalDuration, 0.99));

      if (elapsed >= totalDuration) {
        recorder.stop();
        if (onProgress) onProgress(1);
        return;
      }

      renderFrame(ctx, steps, timeline, drawInfos, elapsed);
      requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  });
}

// ── Shared helpers (used by chainGifExport.js too) ───────────────────────────
export { SIZE, FPS, PLAYBACK_SPEED, TYPING_DELAY, TEXT_LINGER, DRAW_LINGER, END_PAUSE };
export { buildSetup, renderFrame };

// ── Main export ───────────────────────────────────────────────────────────────
/**
 * Record the chain reveal as a video.
 *
 * Uses WebCodecs (faster than real-time) when available, falls back to
 * MediaRecorder (real-time) for older browsers.
 *
 * @param {object}   chain      — chain object with .steps[] and .original_author
 * @param {function} onProgress — called with 0–1 as recording progresses
 * @returns {Promise<{blob: Blob, mimeType: string}>}
 */
export async function recordChainVideo(chain, onProgress) {
  const steps = chain.steps || [];
  if (steps.length === 0) return null;

  const useWebCodecs = typeof VideoEncoder !== 'undefined'
    && typeof VideoFrame !== 'undefined';

  if (useWebCodecs) {
    try {
      return await recordWithWebCodecs(chain, onProgress);
    } catch (err) {
      console.warn('WebCodecs export failed, falling back to MediaRecorder:', err);
    }
  }

  return recordWithMediaRecorder(chain, onProgress);
}
